import { NavLink } from "react-router-dom";
import {
  type LucideIcon,
  Building2,
  Coins,
  Globe,
  ShieldCheck,
  User,
  Users as UsersIcon,
} from "lucide-react";

import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n/types";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";

interface NavItem {
  to: string;
  labelKey: TranslationKey;
  descriptionKey: TranslationKey;
  icon: LucideIcon;
}

const ITEMS: ReadonlyArray<NavItem> = [
  {
    to: ROUTES.SETTINGS_PROFILE,
    labelKey: "settings.page.tabProfileLabel",
    descriptionKey: "settings.page.tabProfileDescription",
    icon: User,
  },
  {
    to: ROUTES.SETTINGS_TENANT,
    labelKey: "settings.page.tabTenantLabel",
    descriptionKey: "settings.page.tabTenantDescription",
    icon: Building2,
  },
  {
    to: ROUTES.SETTINGS_USERS,
    labelKey: "settings.page.tabUsersLabel",
    descriptionKey: "settings.page.tabUsersDescription",
    icon: UsersIcon,
  },
  {
    to: ROUTES.SETTINGS_FX_RATE,
    labelKey: "settings.page.tabFxRateLabel",
    descriptionKey: "settings.page.tabFxRateDescription",
    icon: Coins,
  },
  {
    to: ROUTES.SETTINGS_ALLOWED_ORIGINS,
    labelKey: "settings.page.tabAllowedOriginsLabel",
    descriptionKey: "settings.page.tabAllowedOriginsDescription",
    icon: Globe,
  },
  {
    to: ROUTES.SETTINGS_DATA_PROTECTION,
    labelKey: "settings.page.tabDataProtectionLabel",
    descriptionKey: "settings.page.tabDataProtectionDescription",
    icon: ShieldCheck,
  },
];

/**
 * Vertical sidebar navigation for the Settings page. Anthropic
 * Console pattern: left rail of section links + main column for
 * the active tab's content. Active tab carries an ocean-blue left
 * border + ocean-50 background so the section is unambiguous even
 * when the rail is the only visible affordance.
 */
export function SettingsSidebar() {
  const { t } = useTranslation();
  return (
    <aside
      aria-label={t("settings.page.title")}
      className="border-b border-ocean-100 tablet:w-60 tablet:shrink-0 tablet:border-b-0 tablet:border-r"
    >
      <nav className="flex gap-1 overflow-x-auto p-2 tablet:flex-col tablet:gap-0.5 tablet:p-3">
        {ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                cn(
                  "group flex shrink-0 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors tablet:w-full",
                  isActive
                    ? "bg-ocean-50 text-ocean-900 tablet:border-l-2 tablet:border-ocean-600"
                    : "text-neutral-600 hover:bg-ocean-50/50 hover:text-ocean-800"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={16}
                    className={cn(
                      "shrink-0",
                      isActive ? "text-ocean-600" : "text-neutral-400"
                    )}
                  />
                  <div className="min-w-0">
                    <div
                      className={cn(
                        "font-medium",
                        isActive ? "text-ocean-900" : "text-ocean-800"
                      )}
                    >
                      {t(item.labelKey)}
                    </div>
                    <div className="hidden text-xs text-neutral-500 tablet:block">
                      {t(item.descriptionKey)}
                    </div>
                  </div>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
